import { HugeiconsIcon } from "@hugeicons/react";
import { Activity01Icon, GitCompareIcon, OctagonIcon, Resize01Icon, SecurityCheckIcon } from "@hugeicons/core-free-icons";
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { DemoDataBadge } from '@/components/shared/DemoDataBadge';
import { MotionSection, StaggerContainer, StaggerItem } from '@/components/shared/MotionSection';
import {
  OperationalPageHeader,
  OperationalStatCard,
  operationalCardClass,
} from '@/components/shared/OperationalUI';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';

export default function Assurance() {
  const [monitors, setMonitors] = useState(null);

  useEffect(() => {
    api.get('/operating-system').then(({ data }) => setMonitors(data.assurance_monitors));
  }, []);

  if (!monitors) {
    return (
      <div className="mx-auto grid max-w-7xl gap-4 px-6 py-10 md:grid-cols-2">
        {[...Array(4)].map((_, index) => (
          <Skeleton key={index} className="h-72 rounded-2xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 py-10">
      <OperationalPageHeader
        title="Vigilancia y aseguramiento continuo"
        subtitle="Calidad, sesgo, deriva, costo y supervisión humana durante todo el ciclo de vida."
        actions={<DemoDataBadge />}
      />

      <StaggerContainer className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.04}>
        <StaggerItem>
          <OperationalStatCard
            icon={Activity01Icon}
            label="Monitores definidos"
            value={monitors.length}
            subtitle="Calidad, deriva, equidad y costo"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={SecurityCheckIcon}
            label="Saludables"
            value={monitors.filter((monitor) => monitor.status === 'Saludable').length}
            subtitle="Dentro de sus umbrales"
            tone="success"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={OctagonIcon}
            label="Requieren atención"
            value={monitors.filter((monitor) => monitor.status !== 'Saludable').length}
            subtitle="Revisión antes de escalar"
            tone="alert"
          />
        </StaggerItem>
        <StaggerItem>
          <OperationalStatCard
            icon={Resize01Icon}
            label="Revisión por cohortes"
            value="100"
            suffix="%"
            subtitle="Monitores con segmentación declarada"
            tone="blue"
          />
        </StaggerItem>
      </StaggerContainer>

      <MotionSection delay={0.06}>
        <Alert className="rounded-2xl border-primary/15 bg-primary/[0.03]">
          <HugeiconsIcon icon={SecurityCheckIcon} className="size-4" aria-hidden="true" />
          <AlertTitle>Datos de demostración, controles operables</AlertTitle>
          <AlertDescription>
            Los umbrales y criterios de suspensión están listos para conectarse a telemetría real. Ningún indicador de
            esta vista debe interpretarse como desempeño productivo hasta completar esa conexión.
          </AlertDescription>
        </Alert>
      </MotionSection>

      <StaggerContainer className="grid gap-4 md:grid-cols-2" stagger={0.04}>
        {monitors.map((monitor) => (
          <StaggerItem key={monitor.id}>
          <Card className={`${operationalCardClass} overflow-hidden`}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <HugeiconsIcon icon={Activity01Icon} className="size-5" aria-hidden="true" />
                </div>
                <Badge variant={monitor.status === 'Saludable' ? 'secondary' : 'outline'}>{monitor.status}</Badge>
              </div>
              <CardTitle className="pt-3">{monitor.name}</CardTitle>
              <CardDescription>{monitor.initiative}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
              <div>
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{monitor.metric}</p>
                    <p className="mt-1 font-display text-3xl font-bold">
                      {monitor.value}
                      <span className="ml-1 text-sm font-normal text-muted-foreground">{monitor.unit}</span>
                    </p>
                  </div>
                  <p className="text-right text-xs text-muted-foreground">Umbral {monitor.threshold}</p>
                </div>
                <Progress value={monitor.status === 'Saludable' ? 82 : 64} className="mt-4" />
              </div>
              <div className="grid gap-3 rounded-xl border border-border p-4 text-xs sm:grid-cols-2">
                <Info label="Cohortes revisadas" value={monitor.cohorts} />
                <Info label="Última evaluación" value={monitor.last_run} />
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-3">
                <HugeiconsIcon icon={OctagonIcon} className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
                <div>
                  <p className="text-xs font-semibold">Criterio de acción o suspensión</p>
                  <p className="mt-1 text-xs text-muted-foreground">{monitor.action}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          </StaggerItem>
        ))}
      </StaggerContainer>

      <MotionSection delay={0.12}>
      <section className="grid gap-4 md:grid-cols-3">
        <ControlCard
          icon={GitCompareIcon}
          title="Control de cambios"
          description="Nueva versión, proveedor, prompt, población o fuente de datos obliga a reevaluar impacto y controles."
        />
        <ControlCard
          icon={Resize01Icon}
          title="Revisión por cohortes"
          description="Los promedios no ocultan brechas: cada monitor declara las poblaciones y denominadores observados."
        />
        <ControlCard
          icon={OctagonIcon}
          title="Autoridad de suspensión"
          description="Calidad Clínica y CISO pueden contener una capacidad sin esperar la siguiente sesión del comité."
        />
      </section>
      </MotionSection>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium text-foreground">{value}</p>
    </div>
  );
}

function ControlCard({ icon, title, description }) {
  return (
    <Card className={operationalCardClass}>
      <CardHeader>
        <div className="flex size-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
          <HugeiconsIcon icon={icon} className="size-5" aria-hidden="true" />
        </div>
        <CardTitle className="pt-3">{title}</CardTitle>
        <CardDescription className="leading-relaxed">{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}
